import { Ads } from "../../types";

export const AdDashboard: React.FC<{ ad: Ads }> = ({ ad }) => {
    const views        = ad.views ?? 0;
    const expected     = ad.expected_views ?? 0;
    const priceRaw     = parseFloat((ad.price ?? '0').toString().replace(',', '.')) || 0;
    const pct          = expected > 0 ? Math.round((views / expected) * 100) : 0;
    const cpv          = views > 0 ? (priceRaw / views * 1000).toFixed(2) : null;
    const likes        = ad.post?.total_likes    ?? 0;
    const comments     = ad.post?.total_comments ?? 0;
    const shares       = ad.post?.total_shares   ?? 0;
    const saved        = ad.post?.total_saved    ?? 0;
    const totalEng     = likes + comments + shares + saved;
    const engRate      = views > 0 ? ((totalEng / views) * 100).toFixed(1) : null;

    const fmt = (n: number) => n.toLocaleString('pt-BR');

    const statusColor: Record<string, string> = {
        online:              '#639922',
        off:                 '#888780',
        pendente:            '#BA7517',
        validando_conteudo:  '#185FA5',
    };

    const statusLabel: Record<string, string> = {
        online:              'online',
        off:                 'encerrado',
        pendente:            'aguardando pagamento',
        validando_conteudo:  'validando conteúdo',
    };

    const color = statusColor[ad.status] ?? '#888780';
    const label = statusLabel[ad.status] ?? ad.status;

    return (
        <div style={{ padding: '1.25rem 0' }}>

            {/* header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 12, fontWeight: 500,
                        padding: '4px 12px', borderRadius: 20,
                        background: color + '22', color,
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                        {label}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>anúncio #{ad.id}</span>
                </div>
            </div>

            {/* métricas principais */}
            <p style={{ fontSize: 13, fontWeight: 500, marginBottom: '0.75rem' }}>visão geral</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: '1.25rem' }}>
                {[
                    { label: 'visualizações', value: fmt(views),           sub: `${pct}% da meta` },
                    { label: 'investido',      value: `R$ ${ad.price ?? 0}`, sub: cpv ? `R$ ${cpv} CPM` : '' },
                    { label: 'meta',           value: fmt(expected),        sub: `${fmt(Math.max(0, expected - views))} restantes` },
                    { label: 'custo/1k views', value: cpv ? `R$ ${cpv}` : '—', sub: 'por 1000 views' },
                ].map(m => (
                    <div key={m.label} style={{ textAlign: "center", border: '0.5px solid var(--color-border-tertiary)', borderRadius: 'var(--border-radius-md)', padding: '14px 16px' }}>
                        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 6 }}>{m.label}</p>
                        <p style={{ fontSize: 22, fontWeight: 500 }}>{m.value}</p>
                        <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{m.sub}</p>
                    </div>
                ))}
            </div>

            {/* barra de progresso */}
            <div style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 'var(--border-radius-lg)', padding: '1.1rem 1.25rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>progresso da campanha</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{pct}%</span>
                </div>
                <div style={{ height: 6, textAlign: "center", borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: '#639922', borderRadius: 3, transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                    <span>{fmt(views)} views</span>
                    <span>meta: {fmt(expected)}</span>
                </div>
            </div>

            {/* engajamento */}
            <p style={{ fontSize: 13, fontWeight: 500, marginBottom: '0.75rem' }}>engajamento</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: '1.25rem' }}>
                {[
                    { label: 'curtidas',    value: fmt(likes)    },
                    { label: 'comentários', value: fmt(comments) },
                    { label: 'reposts',     value: fmt(shares)   },
                    { label: 'salvos',      value: fmt(saved)    },
                ].map(e => (
                    <div key={e.label} style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 'var(--border-radius-md)', padding: 12, textAlign: 'center' }}>
                        <p style={{ fontSize: 16, fontWeight: 500 }}>{e.value}</p>
                        <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{e.label}</p>
                    </div>
                ))}
            </div>

            {/* detalhes + CTA */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }} className="detaiuls-ads">
                <div style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 'var(--border-radius-lg)', padding: '1rem 1.25rem' }} className="detaiuls-ads-1">
                    <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>detalhes</p>
                    {[
                        { k: 'status',         v: label },
                        { k: 'preço exibido',  v: ad.price && ad.price !== '0,00' ? `R$ ${ad.price}` : 'não definido' },
                        { k: 'taxa de engaj.', v: engRate ? `${engRate}%` : '—' },
                        { k: 'views restantes',v: fmt(Math.max(0, expected - views)) },
                    ].map(row => (
                        <div key={row.k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '0.5px solid var(--color-border-tertiary)' }}>
                            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.k}</span>
                            <span style={{ fontSize: 13, fontWeight: 500 }}>{row.v}</span>
                        </div>
                    ))}
                </div>

                <div style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 'var(--border-radius-lg)', padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} className="detaiuls-ads-2">
                    <div>
                        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>call to action</p>
                        {[
                            { k: 'botão',   v: ad.button_text ?? '—' },
                            { k: 'destino', v: ad.button_url ? ad.button_url.replace('https://', '') : '—' },
                        ].map(row => (
                            <div key={row.k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '0.5px solid var(--color-border-tertiary)' }}>
                                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.k}</span>
                                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-info)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.v}</span>
                            </div>
                        ))}
                    </div>

                    {ad.status === 'off' && (
                        <button
                            data-bs-toggle="modal" data-bs-target="#increase-limits"
                            style={{ marginTop: 12, fontSize: 13, fontWeight: 500, padding: '8px 14px', borderRadius: 'var(--border-radius-md)', border: '0.5px solid var(--color-border-tertiary)', background: 'var(--color-background-info)', color: 'var(--color-text-info)', cursor: 'pointer' }}
                        >
                            anunciar novamente
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};